import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useLocation } from 'react-router';

import { observer } from 'mobx-react-lite';

import { TOAST_STATUS, VIEW_MODES } from '@evinced-private/ui-common';

import {
	checkRunningScansStateChange,
	getCurrentTab,
	newRunningScans
} from '../../helpers/dashboard/DashboardHelper';
import { useInterval } from '../../hooks/useInterval';
import { IScan } from '../../interfaces/Scan';
import { SCAN_STATE } from '../../interfaces/ScanState';
import {
	COMPARE_TYPE,
	TActivePropertiesList,
	TActiveProperty,
	TActivePropertyDataParams,
	TAverageScoreKpi,
	TPagesMonitoredKpi,
	TQuickWins,
	TScansParams,
	TScansWithStatus,
	TTenantCriticalIssuesKpi
} from '../../pages/dashboard/DashboardTypes';
import {
	DASHBOARD_FILTERS_ID,
	defaultFilterValues
} from '../../pages/dashboard/helpers/DashboardFiltersHelper';
import { HOME_PAGES } from '../../pages/dashboard/HomePageTypes';
import {
	getActivePropertiesData,
	getActivePropertiesList,
	getActivePropertiesQuickWins,
	getCriticalIssuesKpi,
	getPagesMonitoredKpi,
	getScans,
	getScansWithStatus,
	getTenantScoreKpi
} from '../../services/dashboard/DashboardService';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { useCollections } from '../collections/CollectionsProvider';
import { useCommonFiltersStore } from '../commonFiltersProvider/CommonFiltersProvider';
import { useConfiguration } from '../configurationProvider/ConfigurationProvider';
import { useNotifications } from '../notificationsProvider/NotificationsConsumer';
import { INotificationsContext } from '../notificationsProvider/NotificationsContext';

interface IDashboard {
	numberOfRunningScans: number;
	avgScoreKpi: TAverageScoreKpi;
	tenantCriticalIssuesKpi: TTenantCriticalIssuesKpi;
	pageMonitoredKpi: TPagesMonitoredKpi;
	activePropertiesList: TActivePropertiesList;
	numberOfActiveProperties: number;
	activePropertiesData: TActiveProperty[];
	defaultActivePropertiesViews: TActiveProperty[];
	updateActivePropertiesData: (params: TActivePropertyDataParams) => void;
	quickWins: TQuickWins;
	scans: IScan[];
	getScansByPropertyView: ({ viewId, propertyId }: TScansParams) => Promise<TScansWithStatus>;
	liftPeriodCompare: COMPARE_TYPE;
	loadingDuePeriodChangeForTable: boolean;
	loadingDuePeriodChangeForKpis: boolean;
	performanceOverTimeViewMode: VIEW_MODES;
	setPerformanceOverTimeViewMode: React.Dispatch<React.SetStateAction<VIEW_MODES>>;
	refreshDashboardData: () => void;
	isReadyForDataLoading: boolean;
	isActivePropertiesListLoading: boolean;
}

const REFRESH_INTERVAL = 20000;
const DashboardContext = createContext<IDashboard>(null);
const MAX_VIEWS_TO_APPLY = 5;

const DashboardProviderRaw: FC<{ children: ReactNode }> = ({ children }) => {
	const [runningScans, setRunningScans] = useState<IScan[]>([]);
	const [avgScoreKpi, setAvgScoreKpi] = useState<TAverageScoreKpi>(null);
	const [tenantCriticalIssuesKpi, setTenantCriticalIssuesKpi] =
		useState<TTenantCriticalIssuesKpi>(null);
	const [pageMonitoredKpi, setPageMonitoredKpi] = useState<TPagesMonitoredKpi>(null);
	const [activePropertiesList, setActivePropertiesList] = useState<TActivePropertiesList>(null);
	const [activePropertiesData, setActivePropertiesData] = useState<TActivePropertiesList>(null);
	const [defaultActivePropertiesViews, setDefaultActivePropertiesViews] =
		useState<TActivePropertiesList>(null);
	const [quickWins, setQuickWins] = useState<TQuickWins>(null);
	const [scans, setScans] = useState<IScan[]>(null);
	const [loadingDuePeriodChangeForTable, setLoadingDuePeriodChangeForTable] =
		useState<boolean>(true);
	const [loadingDuePeriodChangeForKpis, setLoadingDuePeriodChangeForKpis] = useState<boolean>(true);
	const notificationsContext: INotificationsContext = useNotifications();
	const [performanceOverTimeViewMode, setPerformanceOverTimeViewMode] = useState<VIEW_MODES>(
		VIEW_MODES.GRAPH
	);
	const [isReadyForDataLoading, setIsReadyForDataLoading] = useState(false);
	const [isActivePropertiesListLoading, setIsActivePropertiesListLoading] = useState(false);

	const location = useLocation();
	const { getToggle } = useConfiguration();
	const { showOnlyPropertiesInMyCollection, isCollectionsLoading } = useCollections();
	const commonFiltersStore = useCommonFiltersStore();

	const appliedFilters = commonFiltersStore.getFilterValue(DASHBOARD_FILTERS_ID);

	const filterCollectionIds = useMemo(
		() => appliedFilters?.collections?.map(({ value }) => value as string) || null,
		[appliedFilters]
	);

	const filterLiftPeriod: COMPARE_TYPE = useMemo(
		() =>
			(appliedFilters?.liftPeriods[0]?.value as COMPARE_TYPE) ||
			(defaultFilterValues.liftPeriods[0].value as COMPARE_TYPE),
		[appliedFilters]
	);

	// all isMultipleCollectionsEnabled usage should be removed when multiple collections feature is fully integrated
	const isMultipleCollectionsEnabled = getToggle(SiteScannerUiToggles.ENABLE_MULTIPLE_COLLECTIONS);

	const isRunningScansStateChanged = async (): Promise<void> => {
		if (isReadyForDataLoading) {
			const currentRunningScans = await getScans({
				states: [SCAN_STATE.RUNNING],
				collectionIds: filterCollectionIds,
				showOnlyPropertiesInMyCollection,
				isMultipleCollectionsEnabled
			});
			const failedScans = await getScans({
				states: [SCAN_STATE.FAILED],
				collectionIds: filterCollectionIds,
				showOnlyPropertiesInMyCollection,
				isMultipleCollectionsEnabled
			});
			const newScansRunning = newRunningScans(currentRunningScans, runningScans);
			const stateChanged = checkRunningScansStateChange(
				currentRunningScans,
				runningScans,
				failedScans
			);
			if (stateChanged && getCurrentTab(location.pathname) === HOME_PAGES.DASHBOARD) {
				notificationsContext.toast({
					show: true,
					status: TOAST_STATUS.INFO,
					announcement: 'Scans have completed. Please refresh the page for updated stats.'
				});
			}
			if (newScansRunning || stateChanged) {
				setRunningScans(currentRunningScans);
			}
		}
	};

	const getNumberOfRunningScans = useCallback(async () => {
		const currentRunningScans = await getScans({
			states: [SCAN_STATE.RUNNING],
			collectionIds: filterCollectionIds,
			showOnlyPropertiesInMyCollection,
			isMultipleCollectionsEnabled
		});
		setRunningScans(currentRunningScans);
	}, [filterCollectionIds, showOnlyPropertiesInMyCollection, isMultipleCollectionsEnabled]);

	const getKpis = useCallback(async (): Promise<void> => {
		setLoadingDuePeriodChangeForKpis(true);
		const [avgScoreKpiResult, pageMonitoredKpiResult, tenantCriticalIssuesKpiResult] =
			await Promise.all([
				getTenantScoreKpi(
					filterLiftPeriod,
					filterCollectionIds,
					showOnlyPropertiesInMyCollection,
					isMultipleCollectionsEnabled
				),
				getPagesMonitoredKpi(
					filterLiftPeriod,
					filterCollectionIds,
					showOnlyPropertiesInMyCollection,
					isMultipleCollectionsEnabled
				),
				getCriticalIssuesKpi(
					filterLiftPeriod,
					filterCollectionIds,
					showOnlyPropertiesInMyCollection,
					isMultipleCollectionsEnabled
				)
			]);

		// fix for wrong values from the API
		// EP-7655 - itay todo remove when backend fixed
		const formattedAvgScoreKpiResult: TAverageScoreKpi = {
			score: Number(avgScoreKpiResult?.score) || 0,
			lift: Number(avgScoreKpiResult?.lift) || 0,
			liftPercentage: Number(avgScoreKpiResult?.liftPercentage) || 0
		};

		setAvgScoreKpi(formattedAvgScoreKpiResult);
		setPageMonitoredKpi(pageMonitoredKpiResult);
		setTenantCriticalIssuesKpi(tenantCriticalIssuesKpiResult);
		setLoadingDuePeriodChangeForKpis(false);
	}, [
		filterLiftPeriod,
		filterCollectionIds,
		showOnlyPropertiesInMyCollection,
		isMultipleCollectionsEnabled
	]);

	const handleActivePropertiesListLoadingEnd = useCallback(() => {
		setIsActivePropertiesListLoading(false);
	}, []);

	const updateActivePropertiesList = useCallback(async (): Promise<void> => {
		if (filterCollectionIds !== null) {
			setIsActivePropertiesListLoading(true);
			const activePropertiesListResult = await getActivePropertiesList(
				filterCollectionIds,
				showOnlyPropertiesInMyCollection,
				isMultipleCollectionsEnabled,
				handleActivePropertiesListLoadingEnd
			);
			setActivePropertiesList(activePropertiesListResult);
		}
	}, [
		filterCollectionIds,
		showOnlyPropertiesInMyCollection,
		isMultipleCollectionsEnabled,
		handleActivePropertiesListLoadingEnd
	]);

	const updateActivePropertiesData = useCallback(
		async (params: TActivePropertyDataParams & { updateDefaultView?: boolean }) => {
			setLoadingDuePeriodChangeForTable(true);
			const activePropertiesDataResult = await getActivePropertiesData({
				...params,
				compareType: filterLiftPeriod,
				collectionIds: filterCollectionIds,
				showOnlyPropertiesInMyCollection,
				isMultipleCollectionsEnabled
			});
			setActivePropertiesData(activePropertiesDataResult.properties);
			if (params.updateDefaultView) {
				setDefaultActivePropertiesViews(
					activePropertiesDataResult?.properties.slice(0, MAX_VIEWS_TO_APPLY)
				);
			}
			setLoadingDuePeriodChangeForTable(false);
		},
		[
			filterLiftPeriod,
			filterCollectionIds,
			showOnlyPropertiesInMyCollection,
			isMultipleCollectionsEnabled
		]
	);

	const updateQuickWins = useCallback(async () => {
		const quickWinsResult = await getActivePropertiesQuickWins({
			collectionIds: filterCollectionIds,
			showOnlyPropertiesInMyCollection,
			isMultipleCollectionsEnabled
		});
		setQuickWins(quickWinsResult);
	}, [filterCollectionIds, showOnlyPropertiesInMyCollection, isMultipleCollectionsEnabled]);

	useInterval(isRunningScansStateChanged, REFRESH_INTERVAL);

	const refreshDashboardData = useCallback(async (): Promise<void> => {
		if (isReadyForDataLoading) {
			await Promise.all([
				getKpis(),
				updateActivePropertiesData({
					pageNumber: 0,
					pageSize: 5,
					views: false,
					updateDefaultView: true
				}),
				getNumberOfRunningScans(),
				updateQuickWins(),
				updateActivePropertiesList()
			]);
		}
	}, [
		getKpis,
		getNumberOfRunningScans,
		updateActivePropertiesList,
		updateQuickWins,
		updateActivePropertiesData,
		isReadyForDataLoading
	]);

	const onLoad = useCallback(() => {
		getNumberOfRunningScans();
		updateActivePropertiesList();
		updateQuickWins();
	}, [getNumberOfRunningScans, updateActivePropertiesList, updateQuickWins]);

	useEffect(() => {
		if (isReadyForDataLoading) {
			onLoad();
		}
	}, [onLoad, isReadyForDataLoading]);

	const onLiftPeriodChange = useCallback(() => {
		getKpis();
		updateActivePropertiesData({
			pageNumber: 0,
			pageSize: 5,
			views: false,
			updateDefaultView: true
		});
	}, [getKpis, updateActivePropertiesData]);

	useEffect(() => {
		if (isReadyForDataLoading) {
			onLiftPeriodChange();
		}
	}, [onLiftPeriodChange, isReadyForDataLoading]);

	const getScansByPropertyView = useCallback(
		async ({ viewId, propertyId }) => {
			return getScansWithStatus({
				states: [SCAN_STATE.DONE, SCAN_STATE.DONE_WITHOUT_CSV, SCAN_STATE.DONE_WITHOUT_PAGES_CSV],
				propertyId,
				viewId,
				collectionIds: filterCollectionIds,
				showOnlyPropertiesInMyCollection,
				isMultipleCollectionsEnabled
			});
		},
		[filterCollectionIds, showOnlyPropertiesInMyCollection, isMultipleCollectionsEnabled]
	);

	const updateScans = useCallback(
		async ({ viewId, propertyId }) => {
			const scansResult = await getScans({
				states: [SCAN_STATE.DONE, SCAN_STATE.DONE_WITHOUT_CSV, SCAN_STATE.DONE_WITHOUT_PAGES_CSV],
				propertyId,
				viewId,
				collectionIds: filterCollectionIds,
				showOnlyPropertiesInMyCollection,
				isMultipleCollectionsEnabled
			});
			setScans(scansResult);
		},
		[filterCollectionIds, showOnlyPropertiesInMyCollection, isMultipleCollectionsEnabled]
	);

	useEffect(() => {
		if (isReadyForDataLoading) {
			const viewId = defaultActivePropertiesViews?.[0]?.view?.id;
			const propertyId = defaultActivePropertiesViews?.[0]?.property?.id;
			if (propertyId) {
				updateScans({ viewId, propertyId });
			}
		}
	}, [updateScans, defaultActivePropertiesViews, isReadyForDataLoading]);

	useEffect(() => notificationsContext.toast({ show: false }), [notificationsContext, location]);

	useEffect(() => {
		if (!isCollectionsLoading && filterCollectionIds !== null && !isActivePropertiesListLoading) {
			setIsReadyForDataLoading(true);
		}
	}, [isCollectionsLoading, filterCollectionIds, isActivePropertiesListLoading]);

	// initial filters setup
	useEffect(() => {
		if (!appliedFilters) {
			commonFiltersStore.updateSelectedFilters(DASHBOARD_FILTERS_ID, defaultFilterValues);
		}
	}, [appliedFilters, commonFiltersStore]);

	const value = useMemo(() => {
		return {
			numberOfRunningScans: runningScans?.length,
			avgScoreKpi,
			tenantCriticalIssuesKpi,
			pageMonitoredKpi,
			activePropertiesList,
			numberOfActiveProperties: activePropertiesList?.length,
			activePropertiesData,
			defaultActivePropertiesViews,
			updateActivePropertiesData,
			quickWins,
			scans,
			getScansByPropertyView,
			liftPeriodCompare: filterLiftPeriod,
			loadingDuePeriodChangeForTable,
			loadingDuePeriodChangeForKpis,
			performanceOverTimeViewMode,
			setPerformanceOverTimeViewMode,
			refreshDashboardData,
			isReadyForDataLoading,
			isActivePropertiesListLoading
		};
	}, [
		runningScans,
		avgScoreKpi,
		tenantCriticalIssuesKpi,
		pageMonitoredKpi,
		activePropertiesList,
		activePropertiesData,
		defaultActivePropertiesViews,
		updateActivePropertiesData,
		quickWins,
		scans,
		getScansByPropertyView,
		filterLiftPeriod,
		loadingDuePeriodChangeForTable,
		loadingDuePeriodChangeForKpis,
		performanceOverTimeViewMode,
		setPerformanceOverTimeViewMode,
		refreshDashboardData,
		isReadyForDataLoading,
		isActivePropertiesListLoading
	]);

	return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};

const DashboardProvider = observer(DashboardProviderRaw);

const useDashboard = (): IDashboard => useContext(DashboardContext);

export { DashboardProvider, useDashboard };
